import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, Edit3, Power, LogOut, ArrowLeft, Save, X, Eye, Send } from "lucide-react";
import { EpicomLogo } from "@/components/brand/Logo";
import SectionLabel from "@/components/brand/SectionLabel";
import DesignProjectsAdmin from "@/components/admin/DesignProjectsAdmin";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPLOYMENT_TYPES = ["Contract", "Full-time", "Part-time", "Casual"];
const STATUSES = ["draft", "published", "closed"];

const blankJob = {
  title: "",
  location: "",
  employment_type: "Contract",
  summary: "",
  description: "",
  compensation: "",
  apply_method: "form",
  apply_value: "",
  status: "draft",
  screening_questions: [],
};

const newQuestion = () => ({
  id: `q_${Math.random().toString(36).slice(2, 10)}`,
  label: "",
  type: "yes_no",
  required: false,
});

export default function Admin() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();

  const [enabled, setEnabled] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [loadingJobs, setLoadingJobs] = useState(true);
  const [editing, setEditing] = useState(null); // job id or "new"
  const [form, setForm] = useState(blankJob);
  const [saving, setSaving] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoadingJobs(true);
    try {
      const [s, j] = await Promise.all([
        axios.get(`${API}/settings/careers`),
        axios.get(`${API}/admin/jobs`, { withCredentials: true }),
      ]);
      setEnabled(!!s.data?.enabled);
      setJobs(Array.isArray(j.data) ? j.data : []);
    } catch (e) {
      if (e?.response?.status === 401) {
        navigate("/login", { replace: true });
        return;
      }
      toast.error("Couldn't load jobs.");
    } finally {
      setLoadingJobs(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (loading) return;
    if (!user || !user.is_admin) {
      navigate("/login", { replace: true });
      return;
    }
    fetchAll();
  }, [user, loading, navigate, fetchAll]);

  const toggleEnabled = async () => {
    try {
      const { data } = await axios.put(
        `${API}/admin/settings/careers`,
        { enabled: !enabled },
        { withCredentials: true },
      );
      setEnabled(!!data.enabled);
      toast.success(data.enabled ? "Careers section is LIVE." : "Careers section hidden.");
    } catch {
      toast.error("Toggle failed.");
    }
  };

  const startNew = () => {
    setEditing("new");
    setForm({ ...blankJob });
  };

  const startEdit = (job) => {
    setEditing(job.id);
    setForm({
      ...blankJob,
      ...job,
      screening_questions: Array.isArray(job.screening_questions)
        ? job.screening_questions
        : [],
    });
  };

  const cancelEdit = () => {
    setEditing(null);
    setForm(blankJob);
  };

  const saveJob = async (e) => {
    e?.preventDefault?.();
    if (!form.title.trim()) {
      toast.error("Title is required.");
      return;
    }
    setSaving(true);
    try {
      if (editing === "new") {
        await axios.post(`${API}/admin/jobs`, form, { withCredentials: true });
        toast.success("Job created.");
      } else {
        await axios.put(`${API}/admin/jobs/${editing}`, form, { withCredentials: true });
        toast.success("Job updated.");
      }
      cancelEdit();
      fetchAll();
    } catch {
      toast.error("Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const deleteJob = async (job) => {
    if (!window.confirm(`Delete "${job.title}"? This can't be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/jobs/${job.id}`, { withCredentials: true });
      toast.success("Job deleted.");
      fetchAll();
    } catch {
      toast.error("Delete failed.");
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg-primary text-white">
        <div className="font-mono text-xs tracking-[0.25em] text-steel-light uppercase">
          Loading…
        </div>
      </div>
    );
  }

  return (
    <div data-testid="admin-page" className="min-h-screen bg-bg-primary text-white">
      <header className="border-b border-steel-dark bg-bg-primary/95 backdrop-blur-md sticky top-0 z-30">
        <div className="h-[2px] w-full bg-signal" />
        <nav className="max-w-[1300px] mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
          <div className="flex items-center gap-5">
            <Link to="/" className="flex items-center gap-3">
              <EpicomLogo className="h-9 w-auto" />
            </Link>
            <span className="font-mono text-xs tracking-[0.25em] text-steel-light uppercase hidden sm:inline border-l border-steel-dark pl-5">
              / Admin
            </span>
          </div>
          <div className="flex items-center gap-4">
            {user && (
              <span className="hidden md:inline font-mono text-xs tracking-[0.15em] text-steel-light uppercase">
                {user.email}
              </span>
            )}
            <Link
              to="/"
              data-testid="admin-back-site"
              className="hidden sm:inline-flex items-center gap-2 font-mono text-xs tracking-[0.2em] uppercase text-steel-light hover:text-signal transition-colors"
            >
              <ArrowLeft size={12} /> Back to site
            </Link>
            <button
              onClick={handleLogout}
              data-testid="admin-logout-btn"
              className="inline-flex items-center gap-2 bg-transparent border border-steel-dark hover:border-signal hover:text-signal text-white font-bold uppercase tracking-wider text-xs px-4 py-2 transition-colors"
            >
              <LogOut size={14} /> Logout
            </button>
          </div>
        </nav>
      </header>

      <main className="max-w-[1300px] mx-auto px-6 lg:px-10 py-12 md:py-16">
        {/* Toggle */}
        <section className="mb-12 border border-steel-dark bg-bg-secondary p-8 md:p-10">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-5">
            <div>
              <SectionLabel className="mb-3">Careers Section Status</SectionLabel>
              <h2 className="heading text-2xl md:text-3xl text-white uppercase mb-2">
                Public Careers Visibility
              </h2>
              <p className="text-steel-light text-sm max-w-2xl leading-relaxed">
                When ON, the Careers section appears on the public homepage with all <strong>published</strong> jobs.
                When OFF, the entire section is hidden — visitors won't see it at all.
              </p>
            </div>
            <button
              onClick={toggleEnabled}
              data-testid="admin-toggle-careers"
              className={`inline-flex items-center justify-center gap-3 font-bold uppercase tracking-wider px-6 py-4 border-2 transition-all ${
                enabled
                  ? "bg-signal border-signal text-white"
                  : "bg-transparent border-steel-dark text-steel-light hover:border-signal hover:text-signal"
              }`}
            >
              <Power size={16} />
              {enabled ? "Live · ON" : "Hidden · OFF"}
            </button>
          </div>
        </section>

        {/* Job listings */}
        <section className="mb-8">
          <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
            <div>
              <SectionLabel className="mb-3">Job Postings</SectionLabel>
              <h2 className="heading text-2xl md:text-3xl text-white uppercase">
                Manage Roles
              </h2>
            </div>
            {editing === null && (
              <button
                onClick={startNew}
                data-testid="admin-new-job-btn"
                className="inline-flex items-center gap-2 bg-signal hover:bg-signal-hover text-white font-bold uppercase tracking-wider px-5 py-3 transition-colors"
              >
                <Plus size={16} /> New Job
              </button>
            )}
          </div>

          {/* Form */}
          {editing !== null && (
            <form
              onSubmit={saveJob}
              data-testid="admin-job-form"
              className="border border-signal/60 bg-bg-secondary p-6 md:p-8 mb-8 space-y-5"
            >
              <div className="flex items-center justify-between">
                <h3 className="heading text-xl text-white uppercase">
                  {editing === "new" ? "New Job" : "Edit Job"}
                </h3>
                <button
                  type="button"
                  onClick={cancelEdit}
                  className="text-steel-light hover:text-signal transition-colors"
                  aria-label="Cancel"
                >
                  <X size={20} />
                </button>
              </div>

              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Title *">
                  <input
                    type="text"
                    required
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    data-testid="admin-job-title"
                    className="adm-input"
                    placeholder="e.g. Installation & Service Technician"
                  />
                </Field>
                <Field label="Location">
                  <input
                    type="text"
                    value={form.location}
                    onChange={(e) => setForm({ ...form, location: e.target.value })}
                    data-testid="admin-job-location"
                    className="adm-input"
                    placeholder="e.g. Lethbridge & area, AB"
                  />
                </Field>
                <Field label="Employment Type">
                  <select
                    value={form.employment_type}
                    onChange={(e) => setForm({ ...form, employment_type: e.target.value })}
                    data-testid="admin-job-type"
                    className="adm-input"
                  >
                    {EMPLOYMENT_TYPES.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </Field>
                <Field label="Status">
                  <select
                    value={form.status}
                    onChange={(e) => setForm({ ...form, status: e.target.value })}
                    data-testid="admin-job-status"
                    className="adm-input"
                  >
                    {STATUSES.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </Field>
              </div>

              <Field label="Compensation (optional)">
                <input
                  type="text"
                  value={form.compensation}
                  onChange={(e) => setForm({ ...form, compensation: e.target.value })}
                  data-testid="admin-job-comp"
                  className="adm-input"
                  placeholder="e.g. Hourly during training, by-the-job after; referral fees + revenue share"
                />
              </Field>

              <Field label="Short Summary (1–2 lines for the list view)">
                <textarea
                  rows={2}
                  value={form.summary}
                  onChange={(e) => setForm({ ...form, summary: e.target.value })}
                  data-testid="admin-job-summary"
                  className="adm-input resize-none"
                  placeholder="A one-liner that appears under the title."
                />
              </Field>

              <Field label="Full Description">
                <textarea
                  rows={10}
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  data-testid="admin-job-description"
                  className="adm-input resize-none"
                  placeholder="Use line breaks for paragraphs and bullet points."
                />
              </Field>

              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="How to Apply">
                  <select
                    value={form.apply_method}
                    onChange={(e) => setForm({ ...form, apply_method: e.target.value })}
                    data-testid="admin-job-apply-method"
                    className="adm-input"
                  >
                    <option value="form">Application form (resume upload)</option>
                    <option value="email">Email an address</option>
                    <option value="url">External URL</option>
                  </select>
                </Field>
                {form.apply_method !== "form" && (
                  <Field label={form.apply_method === "url" ? "Apply URL" : "Apply Email"}>
                    <input
                      type={form.apply_method === "url" ? "url" : "email"}
                      value={form.apply_value}
                      onChange={(e) => setForm({ ...form, apply_value: e.target.value })}
                      data-testid="admin-job-apply-value"
                      className="adm-input"
                      placeholder={form.apply_method === "url" ? "https://…" : "rod@epicom.ca"}
                    />
                  </Field>
                )}
              </div>

              {form.apply_method === "form" && (
                <div className="border border-steel-dark bg-bg-primary/40 p-5 space-y-4">
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div>
                      <div className="font-mono text-[10px] tracking-[0.25em] text-signal uppercase mb-1">
                        Screening Questions (optional)
                      </div>
                      <p className="text-steel-light text-xs leading-relaxed max-w-xl">
                        Add questions applicants must answer alongside name, email, phone, cover note, and resume.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() =>
                        setForm({
                          ...form,
                          screening_questions: [...(form.screening_questions || []), newQuestion()],
                        })
                      }
                      data-testid="admin-add-question"
                      className="inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.2em] uppercase text-steel-light hover:text-signal border border-steel-dark hover:border-signal px-3 py-2 transition-colors"
                    >
                      <Plus size={12} /> Add Question
                    </button>
                  </div>
                  {(form.screening_questions || []).length === 0 ? (
                    <div className="text-steel-light text-xs italic font-mono tracking-[0.1em] uppercase">
                      No screening questions yet.
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {form.screening_questions.map((q, idx) => (
                        <div
                          key={q.id}
                          data-testid={`admin-question-${idx}`}
                          className="grid grid-cols-12 gap-3 items-start"
                        >
                          <div className="col-span-12 sm:col-span-7">
                            <input
                              type="text"
                              value={q.label}
                              onChange={(e) => {
                                const next = [...form.screening_questions];
                                next[idx] = { ...q, label: e.target.value };
                                setForm({ ...form, screening_questions: next });
                              }}
                              placeholder="e.g. Do you have tower climbing certification?"
                              className="adm-input"
                            />
                          </div>
                          <div className="col-span-6 sm:col-span-2">
                            <select
                              value={q.type}
                              onChange={(e) => {
                                const next = [...form.screening_questions];
                                next[idx] = { ...q, type: e.target.value };
                                setForm({ ...form, screening_questions: next });
                              }}
                              className="adm-input"
                            >
                              <option value="yes_no">Yes / No</option>
                              <option value="yes_no_text">Yes / No + follow-up</option>
                              <option value="short">Short</option>
                              <option value="long">Long</option>
                            </select>
                          </div>
                          <label className="col-span-5 sm:col-span-2 inline-flex items-center gap-2 h-full pt-3 text-steel-light font-mono text-[10px] tracking-[0.2em] uppercase cursor-pointer">
                            <input
                              type="checkbox"
                              checked={!!q.required}
                              onChange={(e) => {
                                const next = [...form.screening_questions];
                                next[idx] = { ...q, required: e.target.checked };
                                setForm({ ...form, screening_questions: next });
                              }}
                              className="accent-signal"
                            />
                            Required
                          </label>
                          <button
                            type="button"
                            onClick={() => {
                              const next = form.screening_questions.filter((_, i) => i !== idx);
                              setForm({ ...form, screening_questions: next });
                            }}
                            data-testid={`admin-question-${idx}-delete`}
                            className="col-span-1 p-2 text-steel-light hover:text-red-400 transition-colors justify-self-end"
                            aria-label="Remove"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <div className="flex items-center justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={cancelEdit}
                  className="font-mono text-xs tracking-[0.2em] uppercase text-steel-light hover:text-signal border border-steel-dark hover:border-signal px-5 py-3 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  data-testid="admin-job-save"
                  className="inline-flex items-center gap-2 bg-signal hover:bg-signal-hover disabled:opacity-50 text-white font-bold uppercase tracking-wider px-6 py-3 transition-colors"
                >
                  <Save size={14} />
                  {saving ? "Saving…" : editing === "new" ? "Create Job" : "Save Changes"}
                </button>
              </div>
            </form>
          )}

          {/* List */}
          {loadingJobs ? (
            <div className="font-mono text-xs tracking-[0.25em] text-steel-light uppercase py-12 text-center">
              Loading jobs…
            </div>
          ) : jobs.length === 0 ? (
            <div className="border border-steel-dark bg-bg-secondary p-10 text-center">
              <p className="text-steel-light mb-5">No job postings yet.</p>
              <button
                onClick={startNew}
                className="inline-flex items-center gap-2 bg-signal hover:bg-signal-hover text-white font-bold uppercase tracking-wider px-5 py-3 transition-colors"
              >
                <Plus size={16} /> Create your first job posting
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-[1px] bg-steel-dark border border-steel-dark">
              {jobs.map((job) => (
                <article
                  key={job.id}
                  data-testid={`admin-job-row-${job.id}`}
                  className="bg-bg-secondary p-6 md:p-8"
                >
                  <div className="flex items-center justify-between mb-3">
                    <span
                      className={`font-mono text-[10px] tracking-[0.25em] uppercase px-3 py-1 border ${
                        job.status === "published"
                          ? "text-signal border-signal/40"
                          : job.status === "closed"
                          ? "text-steel-light border-steel-dark"
                          : "text-steel-light border-steel-dark"
                      }`}
                    >
                      {job.status}
                    </span>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => startEdit(job)}
                        data-testid={`admin-job-edit-${job.id}`}
                        className="p-2 text-steel-light hover:text-signal transition-colors"
                        aria-label="Edit"
                      >
                        <Edit3 size={16} />
                      </button>
                      <button
                        onClick={() => deleteJob(job)}
                        data-testid={`admin-job-delete-${job.id}`}
                        className="p-2 text-steel-light hover:text-red-400 transition-colors"
                        aria-label="Delete"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                  <h3 className="heading text-xl text-white uppercase mb-2 leading-tight">
                    {job.title}
                  </h3>
                  <div className="font-mono text-[10px] tracking-[0.2em] text-steel-light uppercase mb-3">
                    {[job.location, job.employment_type].filter(Boolean).join(" · ")}
                  </div>
                  <div className="flex items-center gap-4 mb-3 font-mono text-[10px] tracking-[0.2em] uppercase">
                    <span
                      className="inline-flex items-center gap-1.5 text-steel-light"
                      data-testid={`admin-job-views-${job.id}`}
                      title="Read More clicks"
                    >
                      <Eye size={12} className="text-signal" />
                      {job.views || 0} views
                    </span>
                    <span
                      className="inline-flex items-center gap-1.5 text-steel-light"
                      data-testid={`admin-job-applies-${job.id}`}
                      title="Applications submitted"
                    >
                      <Send size={12} className="text-signal" />
                      {job.applies || 0} applies
                    </span>
                  </div>
                  {job.summary && (
                    <p className="text-steel-light text-sm leading-relaxed line-clamp-3">
                      {job.summary}
                    </p>
                  )}
                </article>
              ))}
            </div>
          )}
        </section>

        {/* Designs portfolio admin */}
        <DesignProjectsAdmin />
      </main>

      <style>{`
        .adm-input {
          width: 100%;
          background-color: #050505;
          color: #fff;
          border: 1px solid #2A2E33;
          padding: 0.85rem 1rem;
          font-size: 0.875rem;
          transition: border-color 0.2s ease;
        }
        .adm-input:focus {
          outline: none;
          border-color: #E84A1A;
        }
        select.adm-input {
          appearance: none;
          background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23E84A1A' stroke-width='2'><polyline points='6 9 12 15 18 9'/></svg>");
          background-repeat: no-repeat;
          background-position: right 1rem center;
          padding-right: 2.5rem;
        }
      `}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block mb-2 font-mono text-[10px] tracking-[0.25em] text-steel-light uppercase">
        {label}
      </span>
      {children}
    </label>
  );
}
